import { useMediaQuery } from '@mui/material';
import React, { useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { LoadingContext, M_StateActions, MTabsInpage, NavigateContext } from 'src/M_Components';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import NonSpotTenderConfig from './NonSpotTenderConfig';
import TenderPhaseConfig from './TenderPhaseConfig';
import TenderInQuotation from '../Tenders/TenderInQuotation';
import TenderInAnalysis from '../Tenders/TenderInAnalysis';
import TenderFinished from '../Tenders/TenderFinished';
import { hasPermission } from 'src/components/utils/permissionsHelper';

// unified tabs styling
import 'src/pages/tabsInpage.css';

export default function NonSpotTenderPage({ roles, onCreateTab, toastMessages, handleTabClose }) {
    const ctx = useContext(LoadingContext);
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const matches = useMediaQuery('(min-width:900px)');
    const M_State = useSelector((state) => state.StateKeyValue);
    const location = useLocation();
    const { renameTab } = useContext(NavigateContext);
    const selectedTab = useSelector((s) => s.StateKeyValue.find((o) => o.key === 'selectedTab')?.value);

    const [id, setId] = useState(() => M_State.find((obj) => obj.key === 'selectedTab')?.value);

    // Initialize menu and refresh state from session storage
    const [menu, setMenu] = useState(1);
    const [refresh, setRefresh] = useState(false);

    //============================================================================================================
    // EFFECT
    // Rename tab when selectedTab changes
    //============================================================================================================
    useEffect(() => {
        if (!selectedTab) return;
        renameTab(String(selectedTab), t('NonSpotTenders'));
    }, [t, selectedTab, renameTab]);

    //============================================================================================================
    // Function to upsert session data
    //============================================================================================================
    const upsertSession = (tabId, kv) => {
        const current = M_State.find((o) => o.key === tabId)?.value || [];
        const filtered = current.filter((e) => e.key !== kv.key);
        dispatch(M_StateActions.addM_State({ key: tabId, value: [...filtered, kv] }));
    };

    //============================================================================================================
    // Function to get session storage data based on session ID
    //============================================================================================================
    const getSessionStorageData = (sessionID) => {
        if (!sessionID) return;

        ctx.setIsLoad(true);

        const session = M_State.find((obj) => obj.key === sessionID)?.value;
        if (session) {
            const m = session.find((x) => x.key === 'menu')?.value;
            const ref = session.find((x) => x.key === 'refresh')?.value;

            if (typeof m === 'number') {
                setMenu(m);
            } else {
                setMenu(1);
            }

            if (ref !== undefined) {
                setRefresh(ref);
            }
        } else {
            setMenu(1);
        }

        ctx.setIsLoad(false);
    };

    //============================================================================================================
    // EFFECT
    // Effect to get session storage data when the component mounts or pathname changes
    //============================================================================================================
    useEffect(() => {
        if (!id) return;
        getSessionStorageData(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    //============================================================================================================
    // EFFECT
    // Effect to handle tab changes
    //============================================================================================================
    useEffect(() => {
        const newId = M_State.find((obj) => obj.key === 'selectedTab')?.value;
        if (newId && newId !== id) {
            setId(newId);
            getSessionStorageData(newId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [M_State]);

    //============================================================================================================
    // EFFECT
    // Effect to update session storage when menu changes
    //============================================================================================================
    useEffect(() => {
        if (!id) return;
        upsertSession(id, { key: 'menu', value: menu });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, menu]);

    //============================================================================================================
    // EFFECT
    // Effect to update session storage when refresh changes
    //============================================================================================================
    useEffect(() => {
        if (!id) return;
        upsertSession(id, { key: 'refresh', value: refresh });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, refresh]);

    //============================================================================================================
    // Function to determine which tabs to show based on permissions
    //============================================================================================================
    const allowedTabs = () => {
        const tabArray = [
            { id: 1, labelTab: t('NonSpotTendersConfiguration'), iconTab: <i className="pi pi-wrench"></i> },
            { id: 2, labelTab: t('TenderPhases'), iconTab: <i className="pi pi-info-circle"></i> }
        ];

        if (hasPermission(roles, 'Tenders', 'Update') || hasPermission(roles, 'Tenders', 'Admin')) {
            tabArray.push(
                { id: 3, labelTab: t('NonSpotTendersInQuotation'), iconTab: <i className="pi pi-chart-line"></i> },
                { id: 4, labelTab: t('NonSpotTendersInAnalysis'), iconTab: <i className="pi pi-bullseye"></i> },
                { id: 5, labelTab: t('NonSpotTendersFinished'), iconTab: <i className="pi pi-check-square"></i> }
            );
        }

        return tabArray;
    };

    return (
        <div className="tabsInpage divGlobalSettings">
            <div className="tabsInpage__title titleSettings" style={{ color: ctx.secondaryColor }}>
                {t('NonSpotTenders')}
            </div>

            <div className="tabsInpage__body">
                <div className="tabsInpage__sidebar">
                    <MTabsInpage
                        type={matches ? 'column' : 'tabs'}
                        renderTabs={allowedTabs()}
                        firstTabOpen={menu}
                        onGetTab={(obj) => setMenu(obj.id)}
                        fixedArea={false}
                    />
                </div>

                <div className="tabsInpage__content settingsPageMainDiv">
                    {menu === 1 && (
                        <NonSpotTenderConfig
                            roles={roles}
                            onCreateTab={onCreateTab}
                            toastMessages={toastMessages}
                            handleTabClose={handleTabClose}
                        />
                    )}

                    {menu === 2 && (
                        <TenderPhaseConfig
                            roles={roles}
                            onCreateTab={onCreateTab}
                            toastMessages={toastMessages}
                            handleTabClose={handleTabClose}
                        />
                    )}

                    {menu === 3 && (
                        <TenderInQuotation
                            tenderTypeId={2}
                            mode="nonspot"
                            roles={roles}
                            onCreateTab={onCreateTab}
                            toastMessages={toastMessages}
                            handleTabClose={handleTabClose}
                        />
                    )}

                    {menu === 4 && (
                        <TenderInAnalysis
                            tenderTypeId={2}
                            mode="nonspot"
                            roles={roles}
                            onCreateTab={onCreateTab}
                            toastMessages={toastMessages}
                            handleTabClose={handleTabClose}
                        />
                    )}

                    {menu === 5 && (
                        <TenderFinished
                            tenderTypeId={2}
                            mode="nonspot"
                            roles={roles}
                            onCreateTab={onCreateTab}
                            toastMessages={toastMessages}
                            handleTabClose={handleTabClose}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}